import type { ReactNode } from 'react';
import { useContext } from 'react';
import type { CrmTaskInputDTO } from '../../server/dto/crm-task-input-dto.ts';
import {
    LogInteractionTasksContext,
    type LogInteractionTasksContextValue,
    type TaskListItem,
} from './log-interaction-tasks-context.ts';

type LogInteractionTasksProviderProps = LogInteractionTasksContextValue & {
    children: ReactNode;
};

/**
 * Shares the dialog's staged tasks with every section that renders them, so they
 * don't have to be threaded down through the offer list. The form itself stays with
 * the component that owns `useForm` — only the derived items travel.
 */
export function LogInteractionTasksProvider({
    items,
    addTasks,
    children,
}: LogInteractionTasksProviderProps): ReactNode {
    return (
        <LogInteractionTasksContext.Provider value={{ items, addTasks }}>
            {children}
        </LogInteractionTasksContext.Provider>
    );
}

function useLogInteractionTasksContext(): LogInteractionTasksContextValue {
    const value = useContext(LogInteractionTasksContext);
    if (!value) {
        throw new Error('useLogInteractionTasks* must be used inside a LogInteractionTasksProvider');
    }
    return value;
}

/** Every task staged in this log — newly queued ones first. */
export function useStagedTasks(): TaskListItem[] {
    return useLogInteractionTasksContext().items;
}

/** Queues new tasks onto the log, to be saved with it. */
export function useAddStagedTasks(): (tasks: CrmTaskInputDTO[]) => void {
    return useLogInteractionTasksContext().addTasks;
}
